import type { ContentSeed, ContentBrief, ContentDraft } from '../../types';
import type { TableType } from './TableSelector';

interface DataTableProps {
  activeTable: TableType;
  items: (ContentSeed | ContentBrief | ContentDraft)[];
  total: number;
  query: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  sortField: string;
  onSortChange: (field: string) => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function scoreClass(score: number | null): string {
  if (score === null) return '';
  if (score >= 0.6) return 'high';
  if (score >= 0.3) return 'mid';
  return 'low';
}

function SeedRow({ item, selected, onSelect }: { item: ContentSeed; selected: boolean; onSelect: () => void }) {
  const summary = item.full_summary || item.extracted_snippet || '';
  return (
    <tr class={selected ? 'selected' : ''} onClick={onSelect}>
      <td class="cell-date">{formatDate(item.timestamp)}</td>
      <td>
        <div class="cell-title">{item.source_name || '—'}</div>
        {item.source_url && <a class="cell-url" href={item.source_url} target="_blank" rel="noopener">{item.source_url}</a>}
      </td>
      <td>
        <div class="cell-snippet">{summary}</div>
      </td>
      <td>
        {item.domain && <span class="domain-badge">{item.domain}</span>}
      </td>
      <td>
        {item.relevance_score !== null && (
          <div class="cell-score">
            <div class="cell-score-bar">
              <div class={`cell-score-fill ${scoreClass(item.relevance_score)}`} style={{ width: `${(item.relevance_score || 0) * 100}%` }}></div>
            </div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{item.relevance_score?.toFixed(2)}</span>
          </div>
        )}
      </td>
      <td><span class={`status-badge status-${item.status.toLowerCase().replace(/\s+/g, '')}`}>{item.status}</span></td>
    </tr>
  );
}

function BriefRow({ item, selected, onSelect }: { item: ContentBrief; selected: boolean; onSelect: () => void }) {
  return (
    <tr class={selected ? 'selected' : ''} onClick={onSelect}>
      <td class="cell-date">{formatDate(item.created_at)}</td>
      <td><div class="cell-headline">{item.headline}</div></td>
      <td><span class={`channel-badge channel-${item.channel?.toLowerCase()}`}>{item.channel}</span></td>
      <td>
        <div class="cell-score">
          <div class="cell-score-bar">
            <div class={`cell-score-fill ${scoreClass(item.composite_priority)}`} style={{ width: `${(item.composite_priority || 0) * 100}%` }}></div>
          </div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{item.composite_priority?.toFixed(2)}</span>
        </div>
      </td>
      <td><span class={`status-badge status-${item.status.toLowerCase().replace(/\s+/g, '')}`}>{item.status}</span></td>
    </tr>
  );
}

function DraftRow({ item, selected, onSelect }: { item: ContentDraft; selected: boolean; onSelect: () => void }) {
  return (
    <tr class={selected ? 'selected' : ''} onClick={onSelect}>
      <td class="cell-date">{formatDate(item.created_at)}</td>
      <td><div class="cell-headline">{item.headline}</div></td>
      <td><span class={`channel-badge channel-${item.channel?.toLowerCase()}`}>{item.channel}</span></td>
      <td style={{ fontFamily: 'var(--mono)', fontSize: '11px' }}>{item.word_count?.toLocaleString()}</td>
      <td><span class={`status-badge status-${item.status.toLowerCase().replace(/\s+/g, '')}`}>{item.status}</span></td>
    </tr>
  );
}

const SORT_OPTIONS: Record<TableType, { value: string; label: string }[]> = {
  seeds: [
    { value: 'timestamp', label: 'Date (Newest)' },
    { value: 'relevance_score', label: 'Relevance Score' },
    { value: 'source_name', label: 'Source' },
  ],
  briefs: [
    { value: 'created_at', label: 'Date (Newest)' },
    { value: 'composite_priority', label: 'Priority' },
    { value: 'channel', label: 'Channel' },
  ],
  drafts: [
    { value: 'created_at', label: 'Date (Newest)' },
    { value: 'word_count', label: 'Word Count' },
    { value: 'channel', label: 'Channel' },
  ],
};

export function DataTable({ activeTable, items, total, query, selectedId, onSelect, sortField, onSortChange }: DataTableProps) {
  const tableLabel = activeTable === 'seeds' ? 'seeds' : activeTable === 'briefs' ? 'briefs' : 'drafts';

  return (
    <div class="db-centre">
      <div class="results-toolbar">
        <span>
          Showing <strong>{items.length}</strong> of {total.toLocaleString()} {tableLabel}
          {query && <> matching "<strong>{query}</strong>"</>}
        </span>
        <div class="results-sort">
          <label>Sort:</label>
          <select value={sortField} onChange={(e) => onSortChange((e.target as HTMLSelectElement).value)}>
            {SORT_OPTIONS[activeTable].map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div class="data-table-wrap">
        {items.length === 0 ? (
          <div class="empty-state">
            <div class="empty-state-icon">◇</div>
            <div class="empty-state-text">No {tableLabel} found{query ? ` matching "${query}"` : ''}</div>
          </div>
        ) : (
          <table class="data-table">
            <thead>
              {activeTable === 'seeds' && (
                <tr>
                  <th>Date</th>
                  <th>Source</th>
                  <th>Summary</th>
                  <th>Domain</th>
                  <th>Score</th>
                  <th>Status</th>
                </tr>
              )}
              {activeTable === 'briefs' && (
                <tr>
                  <th>Date</th>
                  <th>Headline</th>
                  <th>Channel</th>
                  <th>Priority</th>
                  <th>Status</th>
                </tr>
              )}
              {activeTable === 'drafts' && (
                <tr>
                  <th>Date</th>
                  <th>Headline</th>
                  <th>Channel</th>
                  <th>Words</th>
                  <th>Status</th>
                </tr>
              )}
            </thead>
            <tbody>
              {activeTable === 'seeds' && (items as ContentSeed[]).map((item) => (
                <SeedRow key={item.id} item={item} selected={selectedId === item.id} onSelect={() => onSelect(item.id)} />
              ))}
              {activeTable === 'briefs' && (items as ContentBrief[]).map((item) => (
                <BriefRow key={item.id} item={item} selected={selectedId === item.id} onSelect={() => onSelect(item.id)} />
              ))}
              {activeTable === 'drafts' && (items as ContentDraft[]).map((item) => (
                <DraftRow key={item.id} item={item} selected={selectedId === item.id} onSelect={() => onSelect(item.id)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
